import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BrochureDownload from "@/components/BrochureDownload";
import {
  Building2,
  Users,
  Clock,
  Shield,
  Award,
  Zap,
  Globe,
  Target,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  DollarSign
} from "lucide-react";

const ODC = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "Cost Optimization",
      description: "Save up to 60% on development costs while maintaining high quality standards"
    },
    {
      icon: Users,
      title: "Dedicated Team",
      description: "Get access to a dedicated team of experts working exclusively on your projects"
    },
    {
      icon: Clock,
      title: "24/7 Operations",
      description: "Round-the-clock development and support across multiple time zones"
    },
    {
      icon: Shield,
      title: "Data Security",
      description: "Enterprise-grade security measures and compliance with international standards"
    },
    {
      icon: Zap,
      title: "Faster Time-to-Market",
      description: "Accelerate your product development with our agile methodologies"
    },
    {
      icon: Target,
      title: "Scalable Solutions",
      description: "Easily scale your team up or down based on project requirements"
    }
  ];

  const services = [
    "Custom Software Development",
    "Mobile App Development",
    "Web Application Development",
    "Cloud Migration & DevOps",
    "Quality Assurance & Testing",
    "UI/UX Design Services",
    "Data Analytics & BI",
    "AI/ML Development",
    "API Development & Integration",
    "Maintenance & Support"
  ];

  const features = [
    "State-of-the-art infrastructure",
    "ISO 27001 certified facility",
    "Redundant internet connectivity",
    "24/7 power backup systems",
    "Advanced security protocols",
    "Collaborative workspaces",
    "Modern development tools",
    "Agile project management"
  ];

  return (
    <section id="odc" className="py-12 md:py-16 lg:py-20 bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-duo-gradient bg-clip-text text-transparent pb-2">
            Offshore Development Center
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Establish your dedicated offshore development center with our world-class infrastructure and expert teams
          </p>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
          <div className="text-center">
            <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-2">20+</div>
            <div className="text-muted-foreground text-sm">Global Clients</div>
          </div>
          <div className="text-center">
            <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-2">150+</div>
            <div className="text-muted-foreground text-sm">Skilled Developers</div>
          </div>
          <div className="text-center">
            <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
              <Award className="h-8 w-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-2">99.8%</div>
            <div className="text-muted-foreground text-sm">Uptime SLA</div>
          </div>
          <div className="text-center">
            <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
              <Globe className="h-8 w-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-2">15+</div>
            <div className="text-muted-foreground text-sm">Countries Served</div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-12 text-foreground">
            Why Choose Our ODC?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <Card
                key={index}
                className="group hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 bg-gradient-card border-0 shadow-card"
              >
                <CardContent className="p-6 md:p-8 text-center">
                  <div className="bg-gradient-primary p-3 rounded-lg w-fit mx-auto mb-4 group-hover:shadow-glow transition-all duration-300">
                    <benefit.icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold mb-3 text-foreground">
                    {benefit.title}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Services & Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
          <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-foreground">
                ODC Services
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <CheckCircle className="h-5 w-5 text-success" />
                    <span className="text-foreground">{service}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-foreground">
                Infrastructure Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <CheckCircle className="h-5 w-5 text-success" />
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Models */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-12 text-foreground">
            Flexible Engagement Models
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300 hover:-translate-y-1">
              <CardHeader className="text-center">
                <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Dedicated Team</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-6">
                  Get a dedicated team of developers working exclusively on your projects with full management control.
                </p>
                <Badge className="bg-primary/10 text-primary hover:text-white border-primary/20">
                  Best for Long-term Projects
                </Badge>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
              <CardHeader className="text-center">
                <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Project-Based</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-6">
                  Fixed scope projects with defined timelines and deliverables. Perfect for specific requirements.
                </p>
                <Badge className="bg-accent/10 text-accent  hover:text-white border-accent/20">
                  Best for Defined Scope
                </Badge>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
              <CardHeader className="text-center">
                <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Hybrid Model</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-6">
                  Combination of dedicated resources and project-based delivery for maximum flexibility.
                </p>
                <Badge className="bg-primary/10 text-primary  hover:text-white border-primary/20">
                  Best for Scalability
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ODC;